import ReleaseActivationSelect, { ReleaseParametersFormValues } from './releaseActivationSelect'
import ExpirationDurationSelect from './expirationDurationSelect'
import { FunctionComponent, useState } from 'react'
import styles from './otherReservationSettings.module.scss'
import Title from '@/components/_custom/Title/title'
import { Table, TableBody, TableCell, TableRow } from '@mui/material'
import DialogActions from '@/components/_custom/DialogActions/DialogActions'
import { useTranslation } from 'react-i18next'
import { ReleaseActivation } from '@/types/Daycare'
import { useAuth } from '@/modules/context/AuthContext'
import { SET_OTHER_RESERVATION_PARAMETERS } from '@/modules/API/daycare/daycareHandler.ts'
import { useMutation } from '@apollo/client'
import ButtonTextIcon from '@/components/_custom/Buttons/ButtonTextIcon/ButtonTextIcon.tsx'
import moment from 'moment/moment'
import { useNotification } from '@/modules/context/NotificationContext.tsx'
import { useCurrentDaycareInfos } from '@/hooks/useCurrentDaycareInfos'
import { useDaycareRight } from '@/hooks/useDaycareRight'

interface OtherReservationSettingsProps {
    onClose?: () => void
    visibleSettings?: ('expiration' | 'release')[]
}

const OtherReservationSettings: FunctionComponent<OtherReservationSettingsProps> = ({
    onClose,
    visibleSettings = ['expiration', 'release']
}) => {
    const { t } = useTranslation()

    const { fetchCurrentDaycare } = useAuth()

    const { currentDaycare: daycare } = useCurrentDaycareInfos()

    const canUpdate: boolean = useDaycareRight('canWriteParamsDaycare')

    const { showNotificationSuccess, showNotificationError } = useNotification()

    const [newExpirationDuration, setNewExpirationDuration] = useState<string>(daycare.expirationDuration)
    const [expirationDuration, setExpirationDuration] = useState<string>(daycare.expirationDuration)

    const [releaseParameters, setReleaseParameters] = useState<ReleaseParametersFormValues>(() => ({
        releaseActivation: daycare.releaseParameters?.releaseActivation || ReleaseActivation.releaseDeactive,
        releaseWeekCountBefore: daycare.releaseParameters?.releaseWeekCountBefore || 0,
        releaseDayCountBefore: daycare.releaseParameters?.releaseDayCountBefore || 1,
        releaseDay: daycare.releaseParameters?.releaseDay?.toString() || '0',
        releaseHour: moment(daycare.releaseParameters?.releaseHour || '00:00:00', 'HH:mm:ss').format('HH:mm')
    }))
    const [savedReleaseParameters, setSavedReleaseParameters] = useState(releaseParameters)

    const [setOtherReservationParameters] = useMutation(SET_OTHER_RESERVATION_PARAMETERS, {
        variables: {
            daycareId: daycare.id,
            expirationDuration: expirationDuration,
            releaseActivation: releaseParameters.releaseActivation,
            releaseWeekCountBefore: Number(releaseParameters.releaseWeekCountBefore),
            releaseDayCountBefore: Number(releaseParameters.releaseDayCountBefore),
            releaseHour: releaseParameters.releaseHour,
            releaseDay: Number(releaseParameters.releaseDay)
        },
        onCompleted: () => {
            fetchCurrentDaycare()

            setNewExpirationDuration(expirationDuration)
            setSavedReleaseParameters(releaseParameters)

            showNotificationSuccess(t('otherReservationsSettingsUpdated'))
            onClose?.()
        },
        onError: () => {
            showNotificationError(t('otherReservationsSettingsUpdatedError'))
        }
    })

    const changed: boolean =
        newExpirationDuration !== expirationDuration ||
        (Object.keys(releaseParameters) as (keyof ReleaseParametersFormValues)[]).some(
            (key) => savedReleaseParameters[key] !== releaseParameters[key]
        )

    return (
        <div className={styles.Form}>
            <div className={styles.TitleAndButton}>
                <Title text={t('otherReservationsSettings')} className={styles.Title} />
            </div>
            <div className={styles.Container}>
                <div className={styles.InputContainer}>
                    <Table className={styles.SettingsTable}>
                        <TableBody>
                            {visibleSettings.includes('expiration') && (
                                <TableRow>
                                    <TableCell className={styles.RowText}>{t('expirationAuto')}</TableCell>
                                    <TableCell>
                                        <ExpirationDurationSelect
                                            expirationDuration={expirationDuration}
                                            setExpirationDuration={setExpirationDuration}
                                            canUpdate={canUpdate}
                                        />
                                    </TableCell>
                                </TableRow>
                            )}
                            {visibleSettings.includes('release') && (
                                <ReleaseActivationSelect
                                    releaseParameters={releaseParameters}
                                    onChange={setReleaseParameters}
                                    daycareType={daycare.type}
                                    canUpdate={canUpdate}
                                />
                            )}
                        </TableBody>
                    </Table>
                </div>
            </div>
            {canUpdate && (
                <DialogActions>
                    <ButtonTextIcon
                        type={'update'}
                        onClick={setOtherReservationParameters}
                        disabled={!changed}
                        className={styles.Button}
                        nowrap
                    />
                </DialogActions>
            )}
        </div>
    )
}

export default OtherReservationSettings
